import type {
	ProductEntity,
	ProductsQueryItemModel,
} from '@/entities/assortment'
import type { PricingCampaignModel } from '@/shared/generated'

import { useMemo } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { ANCHORS_TYPES } from '@/entities/anchor'
import { usePriceManager } from '@/entities/assortment'
import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	isGlobalPricingCampaign,
	usePricingCampaignQuery,
} from '@/entities/pricing-campaign'

import MetaValue from '@/shared/components/common/MetaValue'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { LinkButton } from '@/shared/components/ui/link-button'
import { SheetHeader, SheetTitle } from '@/shared/components/ui/sheet'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'
import { getFiltersQueryObject } from '@/shared/tools/location'

import { isProductEntity } from '../lib/helpers'
import { SkuSidebarConfigurableField } from './SkuSidebarConfigurableField'

export const SkuSidebarHeader = ({
	content,
}: {
	content: ProductEntity | ProductsQueryItemModel
}) => {
	const title = (content as ProductEntity).title || content.fields.name

	return (
		<SheetHeader>
			<SheetTitle className='pr-6 text-base wrap-break-word'>
				{title}
			</SheetTitle>
			<SkuSidebarMeta content={content} />
			<SkuSidebarPreviewMeta content={content} />
		</SheetHeader>
	)
}

const AnchorsList = ({
	anchors,
	sku,
}: {
	anchors: { type: string; sku: string; count: number }[]
	sku: string
}) => {
	const { t } = useTranslation()

	const pathname = useLocation({
		select: (location) => location.pathname,
	})

	return (
		<div className='flex items-start gap-1 text-sm'>
			<span>{t('app.anchors', { defaultValue: 'Anchors' })}:</span>
			<div className='flex w-full flex-col overflow-hidden'>
				{anchors.map((anchor) => (
					<div key={anchor.type} className='truncate'>
						{t(`app.${anchor.type}_anchor`, { defaultValue: anchor.type })}{' '}
						<span className='text-muted-foreground'>
							({t('app.is_anchor', { defaultValue: 'Is anchor' })}{' '}
							<LinkButton asChild>
								<Link
									to={pathname}
									search={
										getFiltersQueryObject([
											{
												name: `${anchor.type}_anchor_sku`,
												operation: Operators.IS,
												value: sku,
											},
										]) as any
									}
								>
									{anchor.count}
								</Link>
							</LinkButton>
							, {t('app.anchored_to', { defaultValue: 'Anchored to' })}{' '}
							{anchor.sku ? (
								<LinkButton asChild>
									<Link
										to={pathname}
										search={
											getFiltersQueryObject([
												{
													name: 'sku',
													operation: Operators.IS,
													value: anchor.sku,
												},
											]) as any
										}
										className='truncate hover:underline'
									>
										{anchor.sku}
									</Link>
								</LinkButton>
							) : null}
							)
						</span>
					</div>
				))}
			</div>
		</div>
	)
}
const SkuSidebarMeta = ({
	content,
}: {
	content: ProductEntity | ProductsQueryItemModel
}) => {
	const { t } = useTranslation()
	const anchors = useMemo(() => {
		if (isProductEntity(content)) {
			return getAnchorsData(
				(content.custom_fields || {}) as Record<
					string,
					string | number | (string | number)[] | null
				>
			)
		}

		return []
	}, [content])

	if (isProductEntity(content)) {
		const sku = content.sku
		const productLineId = content.line_id

		return (
			<>
				<div className='flex gap-2'>
					<MetaValue label={t('product_line_id')} value={productLineId} />
					<MetaValue label={t('sku')} value={sku} />
				</div>
				{anchors?.length > 0 ? (
					<AnchorsList anchors={anchors} sku={sku} />
				) : null}
			</>
		)
	} else {
		const productLineId = content?.line_id

		return (
			<div className='flex gap-2'>
				<MetaValue label={t('product_line_id')} value={productLineId} />
			</div>
		)
	}
}

/**
 * Get anchors from product custom fields
 * @param productCustomFields - product custom fields from the product
 * @returns  array of anchors data
 */
function getAnchorsData(
	productCustomFields: Record<
		string,
		number | string | null | (number | string)[]
	>
) {
	return ANCHORS_TYPES.map((anchorType) => {
		const count = productCustomFields?.[`${anchorType}_anchor_count`] as number
		const sku = productCustomFields?.[`${anchorType}_anchor_sku`] as string

		return {
			type: anchorType,
			sku,
			count,
		}
	}).filter(({ count, sku }) => count || sku)
}

const SkuSidebarPreviewMeta = ({
	content,
}: {
	content: ProductEntity | ProductsQueryItemModel
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const pricingCampaignQuery = usePricingCampaignQuery<PricingCampaignModel>(
		content.fields.pricing_campaign_id ?? '',
		{ enabled: !!content.fields.pricing_campaign_id }
	)
	const priceManager = usePriceManager({ content })
	const pricingCampaignName = pricingCampaignQuery.data?.name

	return (
		<DescriptionList className='flex-end mt-4 flex gap-6'>
			<DescriptionGroup>
				<DescriptionTerm className='text-wrap'>
					{t('field_schema_price', { ns: 'fields' })}
				</DescriptionTerm>
				<DescriptionDetail className='font-mono font-bold'>
					{priceManager.initPrice.value && !isNaN(priceManager.initPrice.value)
						? formatNumber(priceManager.initPrice.value, NumberFormats.Fixed)
						: '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm className='text-wrap'>
					{t('field_schema_final_price', { ns: 'fields' })}
				</DescriptionTerm>
				<DescriptionDetail
					className={cn('font-mono font-bold', {
						'text-primary': priceManager.finalPrice.isChanged,
					})}
				>
					{priceManager.finalPrice.value &&
					!isNaN(Number(priceManager.finalPrice.value))
						? formatNumber(
								Number(priceManager.finalPrice.value),
								NumberFormats.Fixed
							)
						: '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<ErrorBoundary fallback={<div />}>
				<SkuSidebarConfigurableField
					configKey='new_margin_field_id'
					content={content}
				/>
			</ErrorBoundary>
			<ErrorBoundary fallback={<div />}>
				<SkuSidebarConfigurableField
					configKey='custom_metric_field_id'
					content={content}
				/>
			</ErrorBoundary>
			{pricingCampaignQuery.data && (
				<DescriptionGroup>
					<DescriptionTerm className='text-wrap'>
						{t('pricing_campaign', { defaultValue: 'Pricing campaign' })}
					</DescriptionTerm>
					<DescriptionDetail className='flex items-center gap-1'>
						<Tooltip>
							<TooltipTrigger asChild>
								<EngineBadge
									interactive
									className='pointer max-w-40 truncate'
									engine={getPricingCampaignEngine(pricingCampaignQuery.data)}
									onClick={() => {
										ampli.settingsPricingCampaignsViewClick({
											location: 'eastside-header',
										})
										showModal('PRICING_CAMPAIGN_MODAL', {
											pricingCampaignId: pricingCampaignQuery.data.id,
										})
									}}
								>
									<span className='truncate'>
										{pricingCampaignName || t('pc_new_products')}
									</span>
								</EngineBadge>
							</TooltipTrigger>
							<TooltipContent>
								{pricingCampaignName || t('pc_new_products')}
							</TooltipContent>
						</Tooltip>
						{isGlobalPricingCampaign(pricingCampaignQuery.data) && (
							<GlobalPcBadge />
						)}
					</DescriptionDetail>
				</DescriptionGroup>
			)}
		</DescriptionList>
	)
}
